import {createReducer, on} from '@ngrx/store';
import {ZahlungenTableActions} from '../actions/zahlungen-table.actions';
import {ZahlungActions} from '../actions/zahlung.actions';
import {ZahlungenTableState} from '../../interfaces/zahlungen-table-state.interface';
import {TableSettings} from '../../interfaces/table-setting.interface';
import {ZahlungListColumn} from '../../modules/overview/table/zahlung-table.columns';
import {InhaberEntitiesActions} from '@adnova/jf-ng-components';
import {FilterChipInputIdentifier} from '../../interfaces/filter-chip-input-identifier.interface';
import {FaelligkeitsSchnellauswahl} from '../../interfaces/faelligkeits-schnellauswahl.enum';
import {v4 as uuid} from 'uuid';
import * as moment from 'moment';


const initialTableSettings: TableSettings = {
  _key: 'zahlungen-app-zahlungen-table-settings-2026-03-09_1',
  _columns: [
    {id: ZahlungListColumn.Checkbox, name: '', isActive: true, sortable: false, disableRowClick: true},
    {id: ZahlungListColumn.Status, name: 'Status', isActive: true, sortable: false},
    {
      id: ZahlungListColumn.Empfaenger,
      name: 'Empfänger',
      isActive: true,
      sortable: true,
      sortIds: {asc: 'EMPFAENGERASC', desc: 'EMPFAENGERDESC'},
      overflow: 'ellipsis'
    },
    {
      id: ZahlungListColumn.Faelligkeitsdatum,
      name: 'Fälligkeitsdatum',
      isActive: true,
      sortable: true,
      sortIds: {asc: 'FAELLIGKEITSDATUMASC', desc: 'FAELLIGKEITSDATUMDESC'}
    },
    {
      id: ZahlungListColumn.Ausfuehrungsdatum,
      name: 'Ausführungsdatum',
      isActive: true,
      sortable: true,
      sortIds: {asc: 'AUSFUEHRUNGSDATUMASC', desc: 'AUSFUEHRUNGSDATUMDESC'}
    },
    {
      id: ZahlungListColumn.Verwendungszweck,
      name: 'Verwendungszweck',
      isActive: true,
      sortable: true,
      sortIds: {asc: 'VERWENDUNGSZWECKASC', desc: 'VERWENDUNGSZWECKDESC'},
      overflow: 'ellipsis'
    },
    {
      id: ZahlungListColumn.Zahlbetrag,
      name: 'Zahlbetrag',
      isActive: true,
      sortable: true,
      sortIds: {asc: 'BETRAGASC', desc: 'BETRAGDESC'},
      alignCell: 'right',
      alignHeader: 'right'
    },
    {
      id: ZahlungListColumn.Erstellt,
      name: 'Erstellt am',
      isActive: true,
      sortable: true,
      sortIds: {asc: 'ERSTELLTASC', desc: 'ERSTELLTDESC'}
    },
    {
      id: ZahlungListColumn.Iban,
      name: 'IBAN',
      isActive: false,
      sortable: true,
      sortIds: {asc: 'IBANASC', desc: 'IBANDESC'}
    },
    {
      id: ZahlungListColumn.Bic,
      name: 'BIC',
      isActive: false,
      sortable: true,
      sortIds: {asc: 'BICASC', desc: 'BICDESC'}
    },
    {
      id: ZahlungListColumn.Bank,
      name: 'Bank',
      isActive: false,
      sortable: true,
      sortIds: {asc: 'BANKASC', desc: 'BANKDESC'}
    },
    {
      id: ZahlungListColumn.Aktion,
      name: '',
      isActive: true,
      sortable: false,
      disableRowClick: true,
    },
  ],
  _sortDirs: ['desc'],
  _sortParams: [ZahlungListColumn.Erstellt],
};

export const initialZahlungenTableState: ZahlungenTableState = {
  selectedIds: [],
  displayedIds: [],
  tableSettings: initialTableSettings,
  ausfuehrungsdatumUserActive: true,
  filterDto: {},
  filterVisible: false,
  chipInputIdentifiers: [],
  faelligkeitsSchnellauswahlSelection: undefined,
};


/*
 * INFO: Filter-Chip Helper-Funktionen
 */

function createFilterChipInputIdentifier(
  filterLabel: string,
  filterName: string,
  filterValue: string | number | boolean,
): FilterChipInputIdentifier {
  return {
    filterId: uuid(),
    filterLabel,
    filterName,
    filterValue,
  };
}

function updateFilterChipInputIdentifier(
  currentFilterChipInputIdentifiers: FilterChipInputIdentifier[],
  filterChipInputIdentifiersToAdd: FilterChipInputIdentifier[],
): FilterChipInputIdentifier[] {
  const result = [...currentFilterChipInputIdentifiers];
  for (const identifierToAdd of filterChipInputIdentifiersToAdd) {
    const indexToReplace = result.findIndex(chip => chip.filterName === identifierToAdd.filterName);
    if (indexToReplace !== -1) {
      result.splice(indexToReplace, 1, identifierToAdd);
    } else {
      result.push(identifierToAdd);
    }
  }
  return result;
}

function deleteFilterChipInputIdentifierByFilterName(
  chipInputIdentifiers: FilterChipInputIdentifier[],
  filterName: string,
): FilterChipInputIdentifier[] {
  return chipInputIdentifiers.filter(chip => chip.filterName !== filterName);
}

function formatDateForChip(dateStr: string): string {
  return moment(dateStr, 'YYYY-MM-DD').format('DD.MM.YYYY');
}

function formatBetragForChip(betrag: number): string {
  return betrag.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
}

function addDays(days: number): string {
  return moment().add(days, 'days').format('YYYY-MM-DD');
}

export const zahlungenTableReducer = createReducer(
  initialZahlungenTableState,

  on(
    ZahlungenTableActions.changeTableSettings,
    (state, action) => ({
      ...state,
      tableSettings: action.tableSettings,
    })),

  on(
    ZahlungenTableActions.setAusfuehrungsdatumUserActive,
    (state, action) => ({
      ...state,
      ausfuehrungsdatumUserActive: action.active,
    })),

  on(
    ZahlungenTableActions.toggleZahlungSelection,
    (state, action) => {
      const selectedIds = [...state.selectedIds];
      const index = selectedIds.indexOf(action.zahlungId);
      if (index > -1) {
        selectedIds.splice(index, 1);
      } else {
        selectedIds.push(action.zahlungId);
      }
      return {
        ...state,
        selectedIds,
      };
    }),

  on(
    ZahlungenTableActions.selectAllZahlungen,
    (state, action) => ({
      ...state,
      selectedIds: [...action.selectableIds],
    })),

  on(
    ZahlungenTableActions.unselectAllZahlungen,
    (state) => ({
      ...state,
      selectedIds: [],
    })),

  /*
   * INFO: Cross-Cutting Reducers
   * Diese reagieren auf Actions aus dem zahlung-Store.
   */

  on(
    ZahlungActions.readZahlungenSuccess,
    (state, action) => ({
      ...state,
      displayedIds: action.zahlungDtos.map(dto => dto.id),
    })),

  on(
    InhaberEntitiesActions.setCurrentInhaberIdFromURL,
    (state) => ({
      ...state,
      selectedIds: [],
    })),

  /*
   * INFO: Filter Reducers
   */

  on(
    ZahlungenTableActions.showFilter,
    (state) => ({
      ...state,
      filterVisible: true,
    })),

  on(
    ZahlungenTableActions.hideFilter,
    (state) => ({
      ...state,
      filterVisible: false,
    })),

  on(
    ZahlungenTableActions.updateSearchFilter,
    (state, {searchValue}) => {
      let updatedChips: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

      if (searchValue.length === 0) {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'suche');
        return {
          ...state,
          filterDto: {...state.filterDto, suche: undefined},
          chipInputIdentifiers: updatedChips,
        };
      }

      const chip = createFilterChipInputIdentifier(searchValue, 'suche', searchValue);
      updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);

      return {
        ...state,
        filterDto: {...state.filterDto, suche: searchValue},
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.updateFaelligkeitsSchnellauswahlFilter,
    (state, {auswahl}) => {
      let updatedChips: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

      // Bestehende Schnellauswahl- und Fälligkeitsdatum-Chips entfernen
      updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'faelligkeitsSchnellauswahl');
      updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'faelligkeitsdatumVon');
      updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'faelligkeitsdatumBis');

      if (!auswahl) {
        return {
          ...state,
          filterDto: {
            ...state.filterDto,
            faelligkeitsdatumVon: undefined,
            faelligkeitsdatumBis: undefined,
          },
          faelligkeitsSchnellauswahlSelection: undefined,
          chipInputIdentifiers: updatedChips,
        };
      }

      let faelligkeitsdatumVon: string | undefined;
      let faelligkeitsdatumBis: string | undefined;
      let chipLabel = '';

      switch (auswahl) {
        case FaelligkeitsSchnellauswahl.DREI_TAGE:
          faelligkeitsdatumBis = addDays(3);
          chipLabel = 'In den nächsten 3 Tagen';
          break;
        case FaelligkeitsSchnellauswahl.SIEBEN_TAGE:
          faelligkeitsdatumBis = addDays(7);
          chipLabel = 'In den nächsten 7 Tagen';
          break;
        case FaelligkeitsSchnellauswahl.ACHT_PLUS_TAGE:
          faelligkeitsdatumVon = addDays(8);
          chipLabel = 'In frühestens 8 Tagen';
          break;
      }

      updatedChips.push(createFilterChipInputIdentifier(chipLabel, 'faelligkeitsSchnellauswahl', auswahl));

      return {
        ...state,
        filterDto: {
          ...state.filterDto,
          faelligkeitsdatumVon,
          faelligkeitsdatumBis,
        },
        faelligkeitsSchnellauswahlSelection: auswahl,
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.updateFaelligkeitsdatumFilter,
    (state, {dateFilter}) => {
      let updatedChips: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

      // Schnellauswahl zurücksetzen bei manueller Datumseingabe
      updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'faelligkeitsSchnellauswahl');

      if (dateFilter.from) {
        const chip = createFilterChipInputIdentifier(
          'Fällig von ' + formatDateForChip(dateFilter.from), 'faelligkeitsdatumVon', dateFilter.from);
        updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);
      } else {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'faelligkeitsdatumVon');
      }

      if (dateFilter.to) {
        const chip = createFilterChipInputIdentifier(
          'Fällig bis ' + formatDateForChip(dateFilter.to), 'faelligkeitsdatumBis', dateFilter.to);
        updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);
      } else {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'faelligkeitsdatumBis');
      }

      return {
        ...state,
        filterDto: {
          ...state.filterDto,
          faelligkeitsdatumVon: dateFilter.from || undefined,
          faelligkeitsdatumBis: dateFilter.to || undefined,
        },
        faelligkeitsSchnellauswahlSelection: undefined,
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.updateAusfuehrungsdatumFilter,
    (state, {dateFilter}) => {
      let updatedChips: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

      if (dateFilter.from) {
        const chip = createFilterChipInputIdentifier(
          'Ausführung von ' + formatDateForChip(dateFilter.from), 'ausfuehrungsdatumVon', dateFilter.from);
        updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);
      } else {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'ausfuehrungsdatumVon');
      }

      if (dateFilter.to) {
        const chip = createFilterChipInputIdentifier(
          'Ausführung bis ' + formatDateForChip(dateFilter.to), 'ausfuehrungsdatumBis', dateFilter.to);
        updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);
      } else {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'ausfuehrungsdatumBis');
      }

      return {
        ...state,
        filterDto: {
          ...state.filterDto,
          ausfuehrungsdatumVon: dateFilter.from || undefined,
          ausfuehrungsdatumBis: dateFilter.to || undefined,
        },
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.setBetragVonFilter,
    (state, {betragVon}) => {
      let updatedChips: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

      if (betragVon !== undefined && betragVon !== null) {
        const chip = createFilterChipInputIdentifier(
          'Betrag von ' + formatBetragForChip(betragVon), 'betragVon', betragVon);
        updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);
      } else {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'betragVon');
      }

      return {
        ...state,
        filterDto: {...state.filterDto, betragVon},
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.setBetragBisFilter,
    (state, {betragBis}) => {
      let updatedChips: FilterChipInputIdentifier[] = [...state.chipInputIdentifiers];

      if (betragBis !== undefined && betragBis !== null) {
        const chip = createFilterChipInputIdentifier(
          'Betrag bis ' + formatBetragForChip(betragBis), 'betragBis', betragBis);
        updatedChips = updateFilterChipInputIdentifier(updatedChips, [chip]);
      } else {
        updatedChips = deleteFilterChipInputIdentifierByFilterName(updatedChips, 'betragBis');
      }

      return {
        ...state,
        filterDto: {...state.filterDto, betragBis},
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.resetSingleFilter,
    (state, {id}) => {
      const identifier = state.chipInputIdentifiers.find(chip => chip.filterId === id);
      if (!identifier) return state;

      const updatedChips = state.chipInputIdentifiers.filter(chip => chip.filterId !== id);
      let newFilterDto = {...state.filterDto};
      let newSchnellauswahlSelection = state.faelligkeitsSchnellauswahlSelection;

      // Spezialbehandlung für Schnellauswahl: auch Datumsfelder zurücksetzen
      if (identifier.filterName === 'faelligkeitsSchnellauswahl') {
        newFilterDto.faelligkeitsdatumVon = undefined;
        newFilterDto.faelligkeitsdatumBis = undefined;
        newSchnellauswahlSelection = undefined;
      } else {
        newFilterDto = {
          ...state.filterDto,
          [identifier.filterName]: undefined,
        };
      }

      return {
        ...state,
        filterDto: newFilterDto,
        faelligkeitsSchnellauswahlSelection: newSchnellauswahlSelection,
        chipInputIdentifiers: updatedChips,
      };
    }),

  on(
    ZahlungenTableActions.resetAllFilter,
    (state) => ({
      ...state,
      filterDto: initialZahlungenTableState.filterDto,
      chipInputIdentifiers: initialZahlungenTableState.chipInputIdentifiers,
      faelligkeitsSchnellauswahlSelection: undefined,
    })),
);
